"use client";

import { useState } from "react";
import { Button } from "@heroui/react";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import Image from "next/image";
import { IAirdropTaskRender } from "@/types/task-renderer.types";
import { getAirdropArgumentValue } from "@/lib/challenges";
import { QuestTaskButton } from "../QuestTaskButton";
import { FEATURED_TASK_STEP_IMAGES, FEATURED_TASK_URL } from "@/config/featured-task";

const TOTAL_STEPS = 4;

const StepDots = ({ current }: { current: number }) => (
  <div className="flex items-center gap-1.5">
    {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
      const idx = i + 1;
      const isActive = idx === current;
      const isDone = idx < current;
      return (
        <div
          key={idx}
          className={`h-1.5 rounded-full transition-all duration-200 ${
            isActive ? "w-6 bg-[#60FF8A]" : isDone ? "w-1.5 bg-[#60FF8A]/60" : "w-1.5 bg-gray-600"
          }`}
        />
      );
    })}
  </div>
);

const StepShell = ({
  step,
  title,
  children,
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  leftSlot,
}: {
  step: number;
  title: string;
  children: React.ReactNode;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  leftSlot?: React.ReactNode;
}) => (
  <div className="space-y-5">
    <div>
      <div className="flex items-center gap-3 mb-2">
        <p className="text-xs uppercase tracking-wider text-gray-400">
          Step {step} of {TOTAL_STEPS}
        </p>
        <StepDots current={step} />
      </div>
      <h3 className="text-xl font-semibold text-white">{title}</h3>
    </div>
    <div>{children}</div>
    <div className="flex items-center justify-between gap-3 pt-2">
      {onBack ? (
        <Button
          variant="bordered"
          className="rounded-lg border-gray-600/40 text-gray-200 hover:bg-gray-800/40"
          startContent={<ArrowLeft className="w-4 h-4" />}
          onPress={onBack}
        >
          Back
        </Button>
      ) : leftSlot ? (
        leftSlot
      ) : (
        <span />
      )}
      {onNext && (
        <Button
          isDisabled={nextDisabled}
          className="rounded-lg bg-[#60FF8A] text-black font-semibold hover:bg-[#60FF8A]/90"
          endContent={<ArrowRight className="w-4 h-4" />}
          onPress={onNext}
        >
          {nextLabel ?? "Next"}
        </Button>
      )}
    </div>
  </div>
);

export const ProductHuntStepRenderer = ({
  task,
  completeTaskMutation,
  campaign,
}: IAirdropTaskRender) => {
  const [step, setStep] = useState(1);

  const campaignTask = campaign.tasks?.find((t) => t.id === task.taskGuid);
  const link =
    (campaignTask?.arguments && getAirdropArgumentValue(campaignTask.arguments, "link")) ||
    FEATURED_TASK_URL;

  const handleOpenProductHunt = () => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const handleClaim = () => {
    completeTaskMutation.mutateAsync({
      campaignId: campaign.id,
      groupId: campaign.campaignGroup,
      taskName: task.taskName,
      taskGuid: task.taskGuid,
    });
  };

  if (step === 1) {
    return (
      <StepShell
        step={1}
        title="Open the Rhuna page on ProductHunt"
        onNext={() => setStep(2)}
        leftSlot={
          <Button
            variant="bordered"
            className="rounded-lg border-[#60FF8A]/40 text-[#60FF8A] hover:bg-[#60FF8A]/10 font-medium"
            startContent={<ExternalLink className="w-4 h-4" />}
            onPress={handleOpenProductHunt}
          >
            Open ProductHunt
          </Button>
        }
      >
        <p className="text-gray-300">
          Head over to the Rhuna ProductHunt page in a new tab. Keep this window open — you&apos;ll come back here to claim your points.
        </p>
      </StepShell>
    );
  }

  if (step === 2) {
    return (
      <StepShell
        step={2}
        title="Sign in or sign up on ProductHunt"
        onBack={() => setStep(1)}
        onNext={() => setStep(3)}
      >
        <p className="text-gray-300 mb-4">
          If you don&apos;t have a ProductHunt account yet, create one — it&apos;s quick. Already signed in? Skip ahead.
        </p>
        <div className="overflow-hidden rounded-xl border border-gray-800/60 bg-black/30">
          <Image
            src={FEATURED_TASK_STEP_IMAGES.step2}
            alt="Sign in to ProductHunt"
            width={1200}
            height={700}
            className="w-full h-auto"
            unoptimized
          />
        </div>
      </StepShell>
    );
  }

  if (step === 3) {
    return (
      <StepShell
        step={3}
        title="Upvote Rhuna"
        onBack={() => setStep(2)}
        onNext={() => setStep(4)}
      >
        <p className="text-gray-300 mb-4">
          Click the Upvote button on the Rhuna ProductHunt page. That&apos;s the move that earns you the 300 points.
        </p>
        <div className="overflow-hidden rounded-xl border border-gray-800/60 bg-black/30">
          <Image
            src={FEATURED_TASK_STEP_IMAGES.step3}
            alt="Upvote Rhuna on ProductHunt"
            width={1200}
            height={700}
            className="w-full h-auto"
            unoptimized
          />
        </div>
      </StepShell>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <p className="text-xs uppercase tracking-wider text-gray-400">
            Step 4 of {TOTAL_STEPS}
          </p>
          <StepDots current={4} />
        </div>
        <h3 className="text-xl font-semibold text-white">Claim your points</h3>
      </div>
      <p className="text-gray-300">
        Once you&apos;ve upvoted Rhuna on ProductHunt, hit claim to collect your{" "}
        <span className="text-[#60FF8A] font-semibold">300 points</span>.
      </p>
      <div className="flex items-center justify-between gap-3 pt-2">
        <Button
          variant="bordered"
          className="rounded-lg border-gray-600/40 text-gray-200 hover:bg-gray-800/40"
          startContent={<ArrowLeft className="w-4 h-4" />}
          onPress={() => setStep(3)}
        >
          Back
        </Button>
        <QuestTaskButton
          task={task}
          taskMutation={completeTaskMutation}
          buttonText="Claim Reward"
          onClick={handleClaim}
        />
      </div>
    </div>
  );
};
